import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';
import { ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITE_URL = 'https://uandfashion.eu';

const BrandsLibraryPage = () => {
  const { language, t } = useLanguage();
  const [spotlights, setSpotlights] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSpotlights = async () => {
      try {
        const res = await axios.get(`${API}/spotlights`);
        setSpotlights(res.data);
      } catch (err) {
        console.error('Failed to load spotlights:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSpotlights();
  }, []);

  const getExcerpt = (sp) => (language === 'nl' ? sp.excerpt_nl : sp.excerpt_en);

  const featured = spotlights.find((sp) => sp.featured);
  const others = spotlights.filter((sp) => !sp.featured);

  return (
    <div className="min-h-screen bg-white" data-testid="brands-library-page">
      <SEO
        title={t(
          'Ontdek merken — merkverhalen | U&Fashion',
          'Discover Brands — Brand Stories | U&Fashion'
        )}
        description={t(
          'Lees de verhalen achter de fashion brands op U&Fashion. Stijl, visie en identiteit van zorgvuldig geselecteerde merken — nieuwe verhalen om de 14 dagen.',
          'Read the stories behind the fashion brands on U&Fashion. Style, vision and identity of carefully selected brands — new stories every two weeks.'
        )}
        canonical={`${SITE_URL}/ontdek-merken`}
      />

      {/* Intro */}
      <section className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-pink-500 mb-3">
            {t('Merkenverhalen', 'Brand stories')}
          </p>
          <h1 className="text-3xl md:text-4xl font-light text-gray-900 mb-5 tracking-tight">
            {t('Ontdek merken', 'Discover brands')}
          </h1>
          <p className="text-gray-600 leading-relaxed">
            {t(
              'Op uandfashion ontdek je een zorgvuldig geselecteerde collectie van fashion brands. In deze rubriek lees je meer over de merken achter de collecties — hun stijl, visie en wat hen uniek maakt. Nieuwe merkverhalen verschijnen om de 14 dagen.',
              'On uandfashion you\'ll discover a carefully curated selection of fashion brands. In this section you can read about the brands behind the collections — their style, vision and what makes them unique. New brand stories appear every two weeks.'
            )}
          </p>
        </div>
      </section>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
        </div>
      ) : spotlights.length === 0 ? (
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <p className="text-gray-500">
            {t(
              'Binnenkort verschijnen hier merkverhalen.',
              'Brand stories will appear here soon.'
            )}
          </p>
        </div>
      ) : (
        <>
          {/* Featured spotlight */}
          {featured && (
            <section className="max-w-5xl mx-auto px-6 py-16" data-testid="featured-spotlight-card">
              <p className="text-xs uppercase tracking-[0.3em] text-pink-500 mb-3 text-center">
                {t('Nu in de kijker', 'Currently featured')}
              </p>
              <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-100 rounded-2xl p-8 md:p-12 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-center gap-8">
                  {featured.logo_image && (
                    <div className="flex-shrink-0 bg-white p-4 rounded-lg border border-gray-100">
                      <img
                        src={featured.logo_image}
                        alt={`${featured.brand_name} logo`}
                        className="h-12 md:h-14 w-auto object-contain"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="flex-1">
                    <h2 className="text-2xl md:text-3xl font-light text-gray-900 mb-3 tracking-tight">
                      {featured.brand_name}
                    </h2>
                    <p className="text-gray-600 leading-relaxed mb-6">
                      {getExcerpt(featured)}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Link
                        to={`/merken/${featured.slug}`}
                        className="inline-flex items-center justify-center gap-2 bg-gray-900 text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors"
                        data-testid="featured-read-story"
                      >
                        {t('Lees het verhaal', 'Read the story')}
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                      <a
                        href={featured.collection_url}
                        target="_blank"
                        rel="noopener noreferrer sponsored"
                        className="inline-flex items-center justify-center gap-2 border border-gray-300 text-gray-800 px-6 py-3 rounded-full text-sm font-medium hover:bg-gray-50 transition-colors"
                        data-testid="featured-view-collection"
                      >
                        {t('Bekijk de collectie', 'View the collection')}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* All other spotlights */}
          {others.length > 0 && (
            <section className="max-w-5xl mx-auto px-6 pb-20" data-testid="all-spotlights">
              <h3 className="text-sm uppercase tracking-[0.2em] text-gray-500 mb-6">
                {t('Alle merkverhalen', 'All brand stories')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {others.map((sp) => (
                  <Link
                    key={sp.id}
                    to={`/merken/${sp.slug}`}
                    className="block bg-white border border-gray-100 rounded-xl p-6 hover:border-pink-200 hover:shadow-md transition-all"
                    data-testid={`spotlight-card-${sp.slug}`}
                  >
                    {sp.logo_image && (
                      <img
                        src={sp.logo_image}
                        alt={`${sp.brand_name} logo`}
                        className="h-8 w-auto object-contain mb-4 opacity-80"
                        loading="lazy"
                      />
                    )}
                    <h4 className="text-xl font-medium text-gray-900 mb-2">
                      {sp.brand_name}
                    </h4>
                    <p className="text-sm text-gray-600 leading-relaxed mb-3">
                      {getExcerpt(sp)}
                    </p>
                    <span className="text-sm text-pink-500 font-medium inline-flex items-center gap-1">
                      {t('Lees het verhaal', 'Read the story')}
                      <ArrowRight className="w-3 h-3" />
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default BrandsLibraryPage;
